import React, { FC, useMemo } from 'react';
import { ProductFilter, ProductMetaData } from '../../../../models/Product';
import { LoanFormat, mapLoanFormat, mapParsedLoanFormat } from '../../../../models/LoanFormat';
import { FilterSetting, ProductSpec } from '../../../store/api';
import Utils from '../../../shared/utils';
import RoutingUtils from '../../../shared/utils/utils-routing';
import { LibxCatalogueProductGroup } from '../../../shared/product/ProductGroup';
import { useTranslation } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../../shared/utils/togglz';
import MetadataUtils from '../../../shared/utils/utils-metadata';

type Props = {
    readonly product: ProductMetaData;
};

export const LibxProductDetailProductGroups: FC<Props> = ({ product }) => {
    return (
        <>
            <SeriesProductGroup loanFormat={LoanFormat.eBooks} product={product} />
            <AuthorProductGroup loanFormat={LoanFormat.eBooks} product={product} />
            <GenreProductGroup loanFormat={LoanFormat.eBooks} product={product} />
        </>
    );
};

type SeriesProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
};

const SeriesProductGroup: FC<SeriesProductGroupProps> = ({ loanFormat, product, language }) => {
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                series: { include: [product.seriesName!] },
                ...languageFilter
            }
        }),
        [product, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled
    });

    const mappedLoanFormat = mapParsedLoanFormat(loanFormat).toLowerCase();
    const loanFormatLabel = t(`loanFormat.plain.${mappedLoanFormat}`, {
        count: 2
    });

    const title =
        loanFormat === mapLoanFormat(product.loanFormat)
            ? language
                ? t(`shop.libx.productGroup.title.moreWithLanguage`, {
                      loanFormat: loanFormatLabel,
                      names: product.seriesName ?? '',
                      language: language
                  })
                : t(`shop.libx.productGroup.title.moreWithoutLanguage`, {
                      loanFormat: loanFormatLabel,
                      names: product.seriesName ?? ''
                  })
            : language
            ? t(`shop.libx.productGroup.title.withLanguage`, {
                  loanFormat: loanFormatLabel,
                  names: product.seriesName ?? '',
                  language: language
              })
            : t(`shop.libx.productGroup.title.withoutLanguage`, {
                  loanFormat: loanFormatLabel,
                  names: product.seriesName ?? ''
              });

    if (!product.seriesName) {
        return null;
    }

    return (
        <LibxCatalogueProductGroup
            productSpec={productSpec}
            seeAllUrl={seeAllUrl}
            title={title.trim()}
            shopLanguage={undefined}
        />
    );
};

type AuthorProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
};

const AuthorProductGroup: FC<AuthorProductGroupProps> = ({ loanFormat, product, language }) => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);

    const authors = useMemo(() => (product.authors ? product.authors.filter(a => !!a.id) : []), [product]);

    const authorNames = authors.map(a => MetadataUtils.getPerformerFullname(a)).join(', ');
    const authorIds = useMemo(() => authors.map(a => a.id!) ?? [], [authors]);

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                author: { include: authorIds },
                ...languageFilter
            }
        }),
        [authorIds, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled
    });
    const mappedLoanFormat = mapParsedLoanFormat(loanFormat).toLowerCase();
    const loanFormatLabel = t(`loanFormat.plain.${mappedLoanFormat}`, {
        count: 2
    });

    const title =
        loanFormat === mapLoanFormat(product.loanFormat)
            ? language
                ? t(`shop.libx.productGroup.title.moreWithLanguage`, {
                      loanFormat: loanFormatLabel,
                      names: authorNames,
                      language: language
                  })
                : t(`shop.libx.productGroup.title.moreWithoutLanguage`, {
                      loanFormat: loanFormatLabel,
                      names: authorNames
                  })
            : language
            ? t(`shop.libx.productGroup.title.withLanguage`, {
                  loanFormat: loanFormatLabel,
                  names: authorNames,
                  language: language
              })
            : t(`shop.libx.productGroup.title.withoutLanguage`, {
                  loanFormat: loanFormatLabel,
                  names: authorNames
              });

    if (!product.authors || product.authors.length === 0) {
        return null;
    }

    return (
        <LibxCatalogueProductGroup
            productSpec={productSpec}
            seeAllUrl={seeAllUrl}
            title={title.trim()}
            shopLanguage={undefined}
        />
    );
};

type GenreProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
};

const GenreProductGroup: FC<GenreProductGroupProps> = ({ loanFormat, product, language }) => {
    const { t } = useTranslation();
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const genreNames = product.genres
        ? product.genres
              .map(g => g.name + (g.subGenres && g.subGenres.length > 0 ? ` > ${g.subGenres[0].name}` : ''))
              .join(', ')
        : '';

    const genreIds = useMemo(
        () =>
            product.genres
                ? product.genres.map(g => (g.subGenres && g.subGenres.length > 0 ? g.subGenres[0].id : g.id))
                : [],
        [product.genres]
    );

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                genre: { include: genreIds },
                ...languageFilter
            }
        }),
        [languageFilter, genreIds, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled
    });
    const mappedLoanFormat = mapParsedLoanFormat(loanFormat).toLowerCase();
    const loanFormatLabel = t(`loanFormat.plain.${mappedLoanFormat}`, {
        count: 2
    });

    const title =
        loanFormat === mapLoanFormat(product.loanFormat)
            ? language
                ? t(`shop.libx.productGroup.title.moreWithLanguage`, {
                      loanFormat: loanFormatLabel,
                      names: genreNames,
                      language: language
                  })
                : t(`shop.libx.productGroup.title.moreWithoutLanguage`, {
                      loanFormat: loanFormatLabel,
                      names: genreNames
                  })
            : language
            ? t(`shop.libx.productGroup.title.withLanguage`, {
                  loanFormat: loanFormatLabel,
                  names: genreNames,
                  language: language
              })
            : t(`shop.libx.productGroup.title.withoutLanguage`, {
                  loanFormat: loanFormatLabel,
                  names: genreNames
              });

    if (!product.genres || product.genres.length === 0) {
        return null;
    }
    return (
        <LibxCatalogueProductGroup
            productSpec={productSpec}
            seeAllUrl={seeAllUrl}
            title={title.trim()}
            shopLanguage={undefined}
        />
    );
};

interface BuildLibxSeeAllUrlParams {
    readonly product: ProductMetaData;
    readonly loanFormat: LoanFormat;
    readonly filter: { [key: string]: FilterSetting };
    readonly targetLanguage?: string;
    readonly isMultiLanguageShopEnabled: boolean;
}

const buildSeeAllUrl = ({
    product,
    loanFormat,
    filter,
    targetLanguage = 'all',
    isMultiLanguageShopEnabled
}: BuildLibxSeeAllUrlParams): string => {
    switch (product.productClass) {
        case 'STANDARD':
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case 'LOTE':
            return RoutingUtils.foreignLanguageDescriptorString({
                language: targetLanguage,
                loanFormat,
                filter,
                ageCategory: undefined
            });
        case 'LANGUAGE_LEARNING':
            return RoutingUtils.languageLearningDescriptorString({
                language: targetLanguage,
                loanFormat,
                filter,
                isMultiLanguageShopEnabled,
                shopLanguage: undefined,
                ageCategory: undefined
            });
        case 'CAMPAIGN':
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case 'LOCAL':
            return '';
    }
};

const buildLanguageFilter = (product: ProductMetaData, language?: string): ProductFilter | undefined => {
    if (!language) return undefined;
    if (!product.language) return undefined;

    if (product.language === language) {
        return { language: { include: [product.language] } };
    }

    return { language: { exclude: [product.language] } };
};
